/**
 * Level 2 — one project, broken down by who we are contracted with.
 */
import { motion } from 'framer-motion';
import { Truck, ChevronRight, FileText, Plus } from 'lucide-react';
import { Panel, fmtINR, ShareBar, RiskPill, riskHue } from './portfolioUi';

export const LevelProject = ({ project, onOpenVendor, onAddVendor }) => {
  const vendors = project.vendors || [];
  const max = Math.max(1, ...vendors.map(v => v.value));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="level-project">
      <Panel className="lg:col-span-8" title="Vendors" hint={`${vendors.length} counterparties on ${project.project_name}`} testId="project-vendors-panel"
             right={
               <button onClick={onAddVendor}
                       className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
                       style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                       data-testid="add-vendor-btn">
                 <Plus className="w-3.5 h-3.5" /> Add vendor
               </button>
             }>
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {vendors.map((v, i) => (
            <motion.button
              key={v.vendor}
              type="button"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i, 10) * 0.03 }}
              whileHover={{ y: -1 }}
              onClick={() => onOpenVendor(v)}
              className="w-full text-left py-4 grid grid-cols-[1fr_auto] gap-4 items-center"
              data-testid={`drilldown-row-vendor-${v.vendor.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} />
                  <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{v.vendor}</span>
                  <RiskPill score={v.avg_risk} />
                </div>
                <div className="mt-2 pr-6"><ShareBar pct={(v.value / max) * 100} hue={riskHue(v.avg_risk)} /></div>
                <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                  {v.contracts_count} contracts · {v.claims} claims · {v.variations} variations · {v.documents} documents · {v.linked_invoices} invoices linked
                </p>
              </div>
              <div className="text-right shrink-0 flex items-center gap-3">
                <div>
                  <p className="text-lg tabular-nums font-light" style={{ color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace' }}>
                    {fmtINR(v.value)}
                  </p>
                  {v.ld_exposure > 0 && <p className="text-[11px] tabular-nums" style={{ color: '#B45309' }}>LD {fmtINR(v.ld_exposure)}</p>}
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              </div>
            </motion.button>
          ))}
        </div>
      </Panel>

      <Panel className="lg:col-span-4" title="Where the paper is" hint="Documents and invoice coverage by vendor" testId="project-coverage-panel">
        <div className="space-y-3">
          {vendors.slice(0, 12).map((v) => (
            <div key={v.vendor} className="flex items-center gap-3" data-testid={`coverage-${v.vendor.replace(/\s+/g, '-').toLowerCase()}`}>
              <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
              <span className="text-xs truncate flex-1" style={{ color: 'var(--text-secondary)' }}>{v.vendor}</span>
              <span className="text-xs tabular-nums" style={{ color: v.documents ? 'var(--text-primary)' : '#B45309' }}>
                {v.documents} docs
              </span>
              <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>{v.linked_invoices} inv</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

export default LevelProject;
